import type { RecipeListItem, RecipeSummary } from '@recipes/contracts';
import { formatMinutes } from '@recipes/domain';
import { Badge, Card, cn } from '@recipes/ui';
import { Clock, Heart, Star, WifiOff } from 'lucide-react';
import { RecipePlaceholder } from '@/features/appearance/recipe-placeholder';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { currentLocale } from '@/lib/i18n';
import { BaseRecipeBadge } from '@/features/recipes/base-recipes/linked-recipe';
import { OriginBadge } from './origin-badge';

export interface RecipeCardProps {
  recipe: RecipeSummary | RecipeListItem;
  /** Override the link target (default: /recipes/:id). */
  to?: string;
  onToggleFavorite?: (recipe: RecipeSummary, next: boolean) => void;
  /** Resolved thumbnail (e.g. blob URL when offline). Falls back to coverThumbnailUrl. */
  thumbnailSrc?: string | null;
  offlineCopy?: boolean;
  /** Extra actions rendered in the footer (restore/purge in the trash view). */
  actions?: ReactNode;
  layout?: 'grid' | 'list';
  className?: string;
}

/** Library / dashboard card: thumbnail, title, time, rating, favorite toggle, origin badge. */
export function RecipeCard({
  recipe,
  to,
  onToggleFavorite,
  thumbnailSrc,
  offlineCopy,
  actions,
  layout = 'grid',
  className,
}: RecipeCardProps) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const src = thumbnailSrc ?? recipe.coverThumbnailUrl;
  const missing = 'missingIngredients' in recipe ? recipe.missingIngredients : undefined;
  const href = to ?? `/recipes/${recipe.id}`;

  return (
    <Card
      className={cn(
        'group relative flex overflow-hidden',
        layout === 'grid' ? 'flex-col' : 'flex-row',
        className,
      )}
      data-testid="recipe-card"
      data-recipe-id={recipe.id}
    >
      <Link
        to={href}
        className={cn(
          'block shrink-0 bg-muted',
          layout === 'grid' ? 'aspect-[4/3] w-full' : 'aspect-square w-28',
        )}
        tabIndex={-1}
        aria-hidden
      >
        {src ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform group-hover:scale-[1.02] motion-reduce:transition-none"
          />
        ) : (
          <RecipePlaceholder recipe={recipe} className="size-full" small={layout !== 'grid'} />
        )}
      </Link>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-base font-semibold leading-snug">
            <Link to={href} className="after:absolute after:inset-0 after:content-['']">
              {recipe.title}
            </Link>
          </h3>
          {onToggleFavorite && (
            <button
              type="button"
              className="relative z-10 -mr-1 -mt-1 inline-flex touch-target items-center justify-center rounded-full hover:bg-accent"
              aria-pressed={recipe.isFavorite}
              aria-label={
                recipe.isFavorite ? t('recipes.card.unfavorite') : t('recipes.card.favorite')
              }
              onClick={(e) => {
                e.preventDefault();
                onToggleFavorite(recipe, !recipe.isFavorite);
              }}
              data-testid="favorite-toggle"
            >
              <Heart
                className={cn('size-5', recipe.isFavorite && 'fill-current text-destructive')}
                aria-hidden
              />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {recipe.totalTimeMinutes !== null && (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" aria-hidden />
              <span>
                <span className="sr-only">{t('recipes.card.totalTime')}: </span>
                {formatMinutes(recipe.totalTimeMinutes, locale)}
              </span>
            </span>
          )}
          {recipe.rating !== null && (
            <span className="inline-flex items-center gap-1">
              <Star className="size-3.5 fill-current text-warning" aria-hidden />
              <span>
                <span className="sr-only">{t('recipes.card.rating')}: </span>
                {recipe.rating.toLocaleString(locale)}
              </span>
            </span>
          )}
          {recipe.difficulty && <span>{t(`recipes.difficulty.${recipe.difficulty}`)}</span>}
          {recipe.status === 'DRAFT' && (
            <Badge variant="outline">{t('recipes.status.DRAFT')}</Badge>
          )}
          {recipe.status === 'ARCHIVED' && (
            <Badge variant="outline">{t('recipes.status.ARCHIVED')}</Badge>
          )}
        </div>
        <div className="mt-auto flex flex-wrap items-center gap-1.5">
          <OriginBadge origin={recipe.origin} sourceDomain={recipe.sourceDomain} />
          {recipe.isBaseRecipe && <BaseRecipeBadge />}
          {offlineCopy && (
            <Badge variant="muted">
              <WifiOff className="mr-1 size-3" aria-hidden />
              {t('offline.badge')}
            </Badge>
          )}
          {missing && missing.length > 0 && (
            <Badge variant="warning" title={missing.join(', ')}>
              {t('recipes.card.missingIngredients', { count: missing.length })}
            </Badge>
          )}
        </div>
        {actions && <div className="relative z-10 mt-2 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </Card>
  );
}
